/**
 * HP heart markup for the DOM hp-pill rows (Fall pill under the flask, Merge heal pill).
 *
 * Cartoon hearts (docs/CARTOON_ART.md): a chubby heart in flat red with one ink outline, a
 * hard ink drop under it, a shade crescent toward the lower right (the shared hard-stop
 * radial paint server `hpHeartFillGrad` in index.html) and a white glint on the upper-left
 * lobe. An empty heart is the same inked outline round a dark hollow. Colours live in
 * cartoon-hud.css so the layers can be retuned without touching markup.
 */

/** A rounder, chubbier heart than the Material one, inset so the ink never clips. */
var HEART_D = "M12 20.6C9.2 18.4 2.6 13.9 2.6 8.6 2.6 5.4 4.9 3.2 7.7 3.2 9.6 3.2 11.1 4.2 12 5.8 12.9 4.2 14.4 3.2 16.3 3.2 19.1 3.2 21.4 5.4 21.4 8.6 21.4 13.9 14.8 18.4 12 20.6Z";
/** Glint on the upper-left lobe: a tilted capsule plus a dot. */
var SHINE_D = "M5.2 8.4C5 6.6 6.3 5.3 8 5.3 8.8 5.3 9.2 6 8.7 6.5 7.6 7.2 6.9 8 6.6 9 6.3 9.8 5.3 9.5 5.2 8.4Z";
var SHINE_DOT = '<circle class="hp-heart__shine" cx="10.4" cy="5.6" r="0.9"/>';

/** @see index.html hidden <defs> — single document-wide gradient id. */
export var HP_HEART_FILL_GRAD_ID = "hpHeartFillGrad";

/**
 * @param {number} maxHp
 * @param {number} hp
 * @param {{ healSlot?: boolean, uid?: number }} [opts]
 * healSlot: show + on the next missing heart from the left (no extra heart).
 * @returns {string}
 */
export function renderHpHeartsHtml(maxHp, hp, opts) {
  opts = opts || {};
  var max = Math.max(1, Math.floor(Number(maxHp) || 3));
  var cur = Math.max(0, Math.min(max, Math.floor(Number(hp) || 0)));
  var html = "";
  for (var i = 0; i < max; i++) {
    var filled = i < cur;
    var isHealTarget = !!opts.healSlot && !filled && i === cur;
    // The first empty heart is the one that was just lost (or the one a repair refills):
    // it gets a little pop each time the row is rebuilt for an HP change.
    var isEdge = !filled && i === cur;
    html += '<span class="hp-heart'
      + (filled ? " hp-heart--filled" : " hp-heart--empty")
      + (isHealTarget ? " hp-heart--heal-slot" : "")
      + (isEdge ? " hp-heart--edge" : "")
      + '">';
    if (isHealTarget) {
      html += '<span class="hp-heart__icon">';
    }
    html += '<svg class="hp-heart__svg" viewBox="0 0 24 24" aria-hidden="true">';
    // Hard ink drop, then the ink outline (stroked at twice the weight, under the body).
    html += '<path class="hp-heart__depth" d="' + HEART_D + '" transform="translate(0 1.4)"/>';
    html += '<path class="hp-heart__rim" d="' + HEART_D + '"/>';
    if (filled) {
      html += '<path class="hp-heart__body" fill="url(#' + HP_HEART_FILL_GRAD_ID + ')" d="' + HEART_D + '"/>';
      html += '<path class="hp-heart__shine" d="' + SHINE_D + '"/>' + SHINE_DOT;
    } else {
      html += '<path class="hp-heart__body" d="' + HEART_D + '"/>';
      html += '<path class="hp-heart__hollow" d="' + HEART_D + '"/>';
    }
    html += "</svg>";
    if (isHealTarget) {
      html += '<span class="hp-heart__plus" aria-hidden="true">+</span>';
      html += "</span>";
    }
    html += "</span>";
  }
  return html;
}
